/**
 * Runtime-injectable Firebase emulator targets.
 *
 * Parallel e2e slots share ONE standalone build while each slot runs a
 * private emulator set on kernel-assigned ports. The serving slot injects its
 * ports at page load via the /api/e2e/emulator-config script, which sets
 * `window.__DD_EMULATOR_CONFIG` before the hydration bundles run. Providers
 * resolve emulator targets through {@link resolveEmulatorTarget}, preferring
 * that runtime config (emulator builds only), then the NEXT_PUBLIC_* values
 * baked at build time, then the standard emulator defaults.
 *
 * Production builds inline `configuration.emulator` to false, so the window
 * global is never read outside emulator builds.
 */
import configuration from '~/configuration';
import { isBrowser } from '~/core/generic/is-browser';

export type EmulatorService = 'firestore' | 'auth' | 'storage';

export interface EmulatorTarget {
  host: string;
  port: number;
}

/**
 * Shape of the per-slot config injected by /api/e2e/emulator-config.
 */
export interface EmulatorRuntimeConfig {
  host: string;
  firestorePort: number;
  authPort: number;
  storagePort: number;
}

export const EMULATOR_RUNTIME_CONFIG_GLOBAL = '__DD_EMULATOR_CONFIG';

type EmulatorRuntimeConfigWindow = Window & {
  __DD_EMULATOR_CONFIG?: unknown;
};

export const DEFAULT_EMULATOR_HOST = '127.0.0.1';

export const DEFAULT_EMULATOR_PORTS: Record<EmulatorService, number> = {
  firestore: 8080,
  auth: 9099,
  storage: 9199,
};

const MIN_PORT = 1;
const MAX_PORT = 65535;

// Loopback aliases the browser cannot reliably reach: under WSL, 'localhost'
// can resolve to ::1 while the emulators listen on IPv4 only, and 0.0.0.0 is
// a bind address, not a connect address.
const LOOPBACK_HOST_ALIASES = new Set(['localhost', '::1', '[::1]', '0.0.0.0']);

function isValidPort(port: number): boolean {
  return Number.isInteger(port) && port >= MIN_PORT && port <= MAX_PORT;
}

function parsePortString(value: string | undefined): number | undefined {
  if (!value || !/^\d+$/.test(value.trim())) {
    return undefined;
  }

  const port = Number.parseInt(value.trim(), 10);

  return isValidPort(port) ? port : undefined;
}

function isValidPortNumber(value: unknown): value is number {
  return typeof value === 'number' && isValidPort(value);
}

export function isEmulatorRuntimeConfig(
  value: unknown,
): value is EmulatorRuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<
    Record<keyof EmulatorRuntimeConfig, unknown>
  >;

  return (
    typeof candidate.host === 'string' &&
    candidate.host.trim().length > 0 &&
    isValidPortNumber(candidate.firestorePort) &&
    isValidPortNumber(candidate.authPort) &&
    isValidPortNumber(candidate.storagePort)
  );
}

/**
 * Reads the per-slot runtime config injected by /api/e2e/emulator-config.
 * Returns undefined on the server, outside emulator builds, or when the
 * global is missing or malformed.
 */
export function readRuntimeEmulatorConfig(): EmulatorRuntimeConfig | undefined {
  // Keep the build-time gate first so production bundles never read the
  // window global.
  if (!configuration.emulator || !isBrowser()) {
    return undefined;
  }

  const candidate = (window as EmulatorRuntimeConfigWindow)[
    EMULATOR_RUNTIME_CONFIG_GLOBAL
  ];

  return isEmulatorRuntimeConfig(candidate) ? candidate : undefined;
}

function getBakedEmulatorHost(): string | undefined {
  // Literal reads only: Next.js inlines explicit NEXT_PUBLIC_* references at
  // build time, never dynamic `process.env[name]` lookups.
  return (
    process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST ?? configuration.emulatorHost
  );
}

function getBakedEmulatorPort(service: EmulatorService): number | undefined {
  switch (service) {
    case 'firestore':
      return parsePortString(process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_PORT);
    case 'auth':
      return parsePortString(
        process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_PORT,
      );
    case 'storage':
      return parsePortString(
        process.env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_PORT,
      );
  }
}

/**
 * Resolves the emulator target for a Firebase service, preferring the
 * runtime-injected per-slot config, then baked NEXT_PUBLIC values, then the
 * standard emulator defaults. Server-side rendering always uses the baked
 * values (the window global does not exist there).
 */
export function resolveEmulatorTarget(
  service: EmulatorService,
): EmulatorTarget {
  const runtimeConfig = readRuntimeEmulatorConfig();

  if (runtimeConfig) {
    const runtimePort =
      service === 'firestore'
        ? runtimeConfig.firestorePort
        : service === 'auth'
          ? runtimeConfig.authPort
          : runtimeConfig.storagePort;

    return { host: runtimeConfig.host, port: runtimePort };
  }

  return {
    host: getBakedEmulatorHost() ?? DEFAULT_EMULATOR_HOST,
    port: getBakedEmulatorPort(service) ?? DEFAULT_EMULATOR_PORTS[service],
  };
}

/**
 * Parses `host:port` env values such as FIRESTORE_EMULATOR_HOST or
 * FIREBASE_STORAGE_EMULATOR_HOST. Accepts an optional http(s) scheme prefix
 * and bracketed IPv6 hosts.
 */
export function parseEmulatorHostEnv(
  value: string | undefined | null,
): EmulatorTarget | undefined {
  if (!value) {
    return undefined;
  }

  const withoutScheme = value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '');

  const separatorIndex = withoutScheme.lastIndexOf(':');

  if (separatorIndex <= 0) {
    return undefined;
  }

  const host = withoutScheme.slice(0, separatorIndex);
  const port = parsePortString(withoutScheme.slice(separatorIndex + 1));

  if (!host || port === undefined) {
    return undefined;
  }

  return { host, port };
}

/**
 * Normalizes loopback host aliases to 127.0.0.1 so browsers always get a
 * connectable IPv4 address.
 */
export function normalizeEmulatorHost(host: string): string {
  const trimmed = host.trim();

  return LOOPBACK_HOST_ALIASES.has(trimmed.toLowerCase())
    ? DEFAULT_EMULATOR_HOST
    : trimmed;
}
